<script setup lang="ts">
/**
 * ShipHeader — Ship icon, editable fit name, save/share/clone buttons
 * Shows at the top of FittingEditor when a fit is loaded.
 */
import { ref, computed, watch } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { updateFit, copyFit, deleteFit, saveFit, rememberFitEditToken } from '../../composables/useApi';

const store = useFitStore();

const emit = defineEmits<{
  'share': [];
}>();

const opponentId = ref('');
const canEdit = computed(() => store.activeFit?.canEdit !== false);

const simUrl = computed(() =>
  `https://evesim.online/?blue=${store.activeFit?.fitID ?? ''}`
);

const compareUrl = computed(() =>
  `https://evesim.online/?blue=${store.activeFit?.fitID ?? ''}&red=${opponentId.value.trim()}`
);

const POPULAR_MATCHUPS: Record<string, Array<{ fitId: number; label: string; note: string }>> = {
  'Leshak': [
    { fitId: 5804, label: 'Vargur', note: 'ASB marauder tank vs spool-up' },
    { fitId: 6554, label: 'Drake', note: 'Missile BC baseline' },
  ],
  'Vargur': [
    { fitId: 6210, label: 'Leshak', note: 'Spool-up into ASB tank' },
    { fitId: 6612, label: 'Deimos', note: 'Blaster HAC brawl' },
  ],
  'Phoenix Navy Issue': [
    { fitId: 6339, label: 'Revelation NI', note: 'Capital siege duel' },
    { fitId: 6554, label: 'Drake', note: 'XL torp subcap application' },
  ],
  'Drake': [
    { fitId: 6210, label: 'Leshak', note: 'Spool-up pressure' },
    { fitId: 6612, label: 'Deimos', note: 'Brawler matchup' },
  ],
  'Deimos': [
    { fitId: 6615, label: 'Sacrilege', note: 'HAC brawl' },
    { fitId: 6210, label: 'Leshak', note: 'Triglavian spool test' },
  ],
  'Sacrilege': [
    { fitId: 6612, label: 'Deimos', note: 'Blaster vs missile HAC' },
    { fitId: 5804, label: 'Vargur', note: 'HAC vs marauder' },
  ],
  'Ikitursa': [
    { fitId: 6612, label: 'Deimos', note: 'T2 Trig vs blaster HAC' },
    { fitId: 6554, label: 'Drake', note: 'Cruiser spool vs BC tank' },
  ],
};

const matchups = computed(() => {
  const shipName = store.activeFit?.shipName;
  if (!shipName) return [];
  return POPULAR_MATCHUPS[shipName] ?? [];
});

function openCompare(): void {
  if (opponentId.value.trim()) {
    void openSimulation(compareUrl.value);
  }
}

async function openSimulation(url: string, event?: MouseEvent): Promise<void> {
  const fit = store.activeFit;
  if (!fit) return;
  if (fit.isPublic === 1) return;
  event?.preventDefault();
  if (!canEdit.value) {
    store.addToast('warning', 'This private fit is read-only. Copy it before simulating.');
    return;
  }

  // Open synchronously so browsers do not treat the post-publish navigation as a popup.
  const target = window.open('about:blank', '_blank');
  try {
    await saveFit(fit.fitID, fit.fitName);
    await store.refreshActiveFit();
    if (target) target.location.href = url;
    else window.location.href = url;
  } catch {
    target?.close();
  }
}

const isEditing = ref(false);
const editName = ref('');

function startEditName(): void {
  const fit = store.activeFit;
  if (!fit || !canEdit.value) return;
  editName.value = fit.fitName;
  isEditing.value = true;
}

async function saveName(): Promise<void> {
  isEditing.value = false;
  const fit = store.activeFit;
  if (!fit || !canEdit.value || editName.value === fit.fitName) return;
  const name = editName.value.trim();
  if (!name) return;
  try {
    await updateFit(fit.fitID, name);
    await store.refreshActiveFit();
    store.addToast('success', 'Fit name updated');
  } catch {
    // Error shown by useApi
  }
}

function handleNameKeydown(e: KeyboardEvent): void {
  if (e.key === 'Enter') {
    saveName();
  } else if (e.key === 'Escape') {
    isEditing.value = false;
  }
}

async function handleCopy(): Promise<void> {
  const fit = store.activeFit;
  if (!fit) return;
  try {
    const { fitID, editToken } = await copyFit(fit.fitID, `${fit.fitName} (copy)`);
    rememberFitEditToken(fitID, editToken);
    const tabId = store.addTab(fitID, `${fit.fitName} (copy)`);
    await store.loadFit(fitID, tabId);
    store.addToast('success', 'Fit copied');
  } catch {
    // Error shown by useApi
  }
}

async function handleDelete(): Promise<void> {
  const fit = store.activeFit;
  if (!fit || !canEdit.value) return;
  try {
    await deleteFit(fit.fitID);
    const tab = store.activeTab;
    if (tab) store.closeTab(tab.id);
    store.addToast('success', 'Fit deleted');
  } catch {
    // Error shown by useApi
  }
}

async function handleSave(): Promise<void> {
  const fit = store.activeFit;
  if (!fit || !canEdit.value) return;
  try {
    await saveFit(fit.fitID, fit.fitName);
    store.addToast('success', 'Fit saved');
  } catch {
    // Error shown by useApi
  }
}

// Keep editName in sync when fit changes
watch(() => store.activeFit?.fitName, (name) => {
  if (name && !isEditing.value) editName.value = name;
});
</script>

<template>
  <div class="ship-header" v-if="store.activeFit">
    <img
      :src="store.iconUrl(store.activeFit.shipTypeID, 64)"
      :alt="store.activeFit.shipName"
      class="ship-icon"
    />

    <div class="header-info">
      <div class="name-row">
        <input
          v-if="isEditing"
          ref="nameInput"
          v-model="editName"
          class="name-input"
          @blur="saveName"
          @keydown="handleNameKeydown"
          @vue:mounted="($event: any) => $event.el?.focus()"
        />
        <h2
          v-else
          class="fit-name"
          @click="startEditName"
          title="Click to rename"
        >
          {{ store.activeFit.fitName }}
        </h2>
      </div>
      <span class="ship-name">{{ store.activeFit.shipName }}</span>
    </div>

    <span v-if="!canEdit" class="read-only-badge" title="Copy this fit to make an editable version">READ ONLY</span>

    <div class="header-actions">
      <button class="save-btn" title="Save fit (Ctrl+S)" :disabled="!canEdit" @click="handleSave">
        <svg width="14" height="14" viewBox="0 0 14 14">
          <path d="M11.5 13H2.5a1 1 0 01-1-1V2a1 1 0 011-1h7.09a1 1 0 01.7.29l1.92 1.92a1 1 0 01.29.7V12a1 1 0 01-1 1z" fill="none" stroke="currentColor" stroke-width="1.2" />
          <rect x="4" y="1" width="5" height="4" rx="0.5" fill="none" stroke="currentColor" stroke-width="1" />
          <rect x="3.5" y="8" width="7" height="4" rx="0.5" fill="none" stroke="currentColor" stroke-width="1" />
        </svg>
        Save
      </button>
      <button class="action-btn" title="Copy fit" @click="handleCopy">
        <svg width="14" height="14" viewBox="0 0 14 14">
          <rect x="4" y="4" width="8" height="8" rx="1" fill="none" stroke="currentColor" stroke-width="1.3" />
          <path d="M2 10V2.5A.5.5 0 012.5 2H10" fill="none" stroke="currentColor" stroke-width="1.3" />
        </svg>
      </button>
      <button class="action-btn" title="Share fit" @click="emit('share')">
        <svg width="14" height="14" viewBox="0 0 14 14">
          <circle cx="3" cy="7" r="1.5" fill="currentColor" />
          <circle cx="11" cy="3" r="1.5" fill="currentColor" />
          <circle cx="11" cy="11" r="1.5" fill="currentColor" />
          <line x1="4.5" y1="6.2" x2="9.5" y2="3.8" stroke="currentColor" stroke-width="1" />
          <line x1="4.5" y1="7.8" x2="9.5" y2="10.2" stroke="currentColor" stroke-width="1" />
        </svg>
      </button>
      <a
        class="sim-btn"
        title="Simulate this fit on evesim.online"
        :href="simUrl"
        target="_blank"
        rel="noopener"
        @click="openSimulation(simUrl, $event)"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 1l6 6-6 6"/><path d="M2 7h11"/></svg>
        Simulate
      </a>
      <div class="compare-group" v-if="store.activeFit?.fitID">
        <input
          type="text"
          class="compare-input"
          v-model="opponentId"
          placeholder="vs Fit ID"
          @keydown.enter="openCompare"
        />
        <a
          v-if="opponentId.trim()"
          class="sim-btn sim-btn--compare"
          title="Compare against this fit on evesim.online"
          :href="compareUrl"
          target="_blank"
          rel="noopener"
          @click="openSimulation(compareUrl, $event)"
        >VS</a>
      </div>
      <button class="action-btn action-btn--danger" title="Delete fit" :disabled="!canEdit" @click="handleDelete">
        <svg width="14" height="14" viewBox="0 0 14 14">
          <path d="M3 4h8l-.7 8.3a1 1 0 01-1 .7H4.7a1 1 0 01-1-.7L3 4z" fill="none" stroke="currentColor" stroke-width="1.3" />
          <line x1="1" y1="4" x2="13" y2="4" stroke="currentColor" stroke-width="1.3" />
          <path d="M5 4V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5V4" fill="none" stroke="currentColor" stroke-width="1.3" />
        </svg>
      </button>
    </div>
    <div v-if="matchups.length > 0" class="popular-matchups">
      <span class="matchups-label">TRY MATCHUPS</span>
      <a
        v-for="m in matchups"
        :key="m.fitId"
        class="matchup-link"
        :href="`https://evesim.online/?blue=${store.activeFit?.fitID}&red=${m.fitId}`"
        target="_blank"
        rel="noopener"
        :title="m.note"
        @click="openSimulation(`https://evesim.online/?blue=${store.activeFit?.fitID}&red=${m.fitId}`, $event)"
      >vs {{ m.label }}</a>
    </div>
  </div>
</template>

<style scoped>
.ship-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  margin-bottom: 12px;
  background:
    linear-gradient(90deg, rgba(0, 212, 255, 0.08), transparent 48%),
    linear-gradient(180deg, var(--bg-panel), var(--bg-darker));
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--glow-panel);
  flex-shrink: 0;
}

.ship-icon {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-md);
  flex-shrink: 0;
  background: var(--bg-panel);
  border: 1px solid var(--border-bright);
  box-shadow: inset 0 0 18px rgba(0, 212, 255, 0.04);
}

.header-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.name-row {
  display: flex;
  align-items: center;
}

.fit-name {
  font-family: var(--font-mono);
  font-size: 1.18rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: var(--text-primary);
  margin: 0;
  cursor: pointer;
  border-bottom: 1px dashed transparent;
  transition: border-color var(--transition-fast), color var(--transition-fast);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fit-name:hover {
  color: var(--accent-blue);
  border-bottom-color: var(--border-accent);
}

.name-input {
  font-family: var(--font-mono);
  font-size: 1.08rem;
  font-weight: 800;
  color: var(--text-primary);
  background: var(--bg-input);
  border: 1px solid var(--accent-blue);
  border-radius: var(--radius-sm);
  padding: 4px 7px;
  outline: none;
  width: 100%;
}

.ship-name {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.read-only-badge {
  color: var(--accent-orange); border: 1px solid rgba(255, 152, 0, .45);
  border-radius: var(--radius-sm); padding: 3px 7px; font: 700 .62rem var(--font-mono);
  letter-spacing: .08em; white-space: nowrap;
}

.header-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.save-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border: 1px solid rgba(76, 175, 80, 0.7);
  border-radius: var(--radius-sm);
  background: linear-gradient(180deg, rgba(76, 175, 80, 0.95), rgba(48, 128, 58, 0.95));
  color: white;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.save-btn:disabled, .action-btn:disabled { opacity: .4; cursor: not-allowed; }

.save-btn:hover:not(:disabled) {
  box-shadow: var(--glow-green);
  transform: translateY(-1px);
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: linear-gradient(180deg, var(--bg-raised), var(--bg-secondary));
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.action-btn:hover {
  color: var(--text-primary);
  border-color: var(--border-accent);
  box-shadow: var(--glow-blue);
  transform: translateY(-1px);
}

.sim-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(0, 212, 255, 0.7);
  border-radius: var(--radius-sm);
  background: linear-gradient(180deg, rgba(0, 212, 255, 0.18), rgba(0, 212, 255, 0.06));
  color: var(--accent-blue);
  font-family: var(--font-mono);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-decoration: none;
  text-transform: uppercase;
  transition: all var(--transition-fast);
}

.sim-link:hover {
  color: #001018;
  background: linear-gradient(180deg, #62e9ff, var(--accent-blue));
  box-shadow: var(--glow-blue);
}

.delete-btn:hover {
  color: var(--accent-red);
  border-color: rgba(244, 67, 54, 0.65);
  box-shadow: 0 0 12px rgba(244, 67, 54, 0.18);
}

@media (max-width: 760px) {
  .ship-header {
    align-items: flex-start;
  }

  .read-only-badge {
  color: var(--accent-orange); border: 1px solid rgba(255, 152, 0, .45);
  border-radius: var(--radius-sm); padding: 3px 7px; font: 700 .62rem var(--font-mono);
  letter-spacing: .08em; white-space: nowrap;
}

.header-actions {
    justify-content: flex-start;
  }
}

.sim-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--accent-blue);
  border-radius: var(--radius-sm);
  background: rgba(0, 212, 255, 0.06);
  color: var(--accent-blue);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-decoration: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.sim-btn:hover {
  background: rgba(0, 212, 255, 0.12);
  color: #fff;
  border-color: #fff;
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.15);
}

.sim-btn svg {
  flex-shrink: 0;
}

.sim-btn--compare {
  padding: 5px 10px;
  font-size: 0.72rem;
}

.compare-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.compare-input {
  width: 80px;
  padding: 5px 8px;
  font-size: 0.75rem;
  background: var(--bg-input, rgba(255,255,255,0.05));
  border: 1px solid var(--border, rgba(255,255,255,0.1));
  border-radius: var(--radius-sm, 4px);
  color: var(--text-primary, #d0d4de);
  font-family: 'JetBrains Mono', monospace;
}

.popular-matchups {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--border, rgba(255,255,255,0.08));
}

.matchups-label {
  font-size: 0.58rem;
  color: var(--text-dim, #6b7280);
  letter-spacing: 0.06em;
  font-weight: 600;
  flex-shrink: 0;
}

.matchup-link {
  display: inline-flex;
  padding: 3px 10px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--accent-blue, #00d4ff);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: var(--radius-sm, 4px);
  text-decoration: none;
  transition: all 0.15s;
}

.matchup-link:hover {
  background: rgba(0, 212, 255, 0.08);
  border-color: rgba(0, 212, 255, 0.4);
  color: #fff;
}

.compare-input::placeholder {
  color: var(--text-dim, #6b7280);
  font-family: inherit;
}
</style>
